import * as Sentry from '@sentry/react';

let sentryInitialized = false;

/**
 * Initialise Sentry. Idempotent. Only runs when VITE_SENTRY_DSN is set.
 *
 * Called by ConsentProvider after the cookie/consent check passes — same
 * pattern as GA4 and Skimlinks. Errors thrown before this point (e.g.
 * during initial bundle parse) won't reach Sentry, which is the
 * deliberate trade-off for staying inside the project's consent model.
 */
export function initSentryIfConfigured(): void {
  if (sentryInitialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  sentryInitialized = true;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,

    integrations: [
      Sentry.browserTracingIntegration(),
      // Default masking is on (maskAllText, blockAllMedia) — replays
      // routinely capture rendered UI including emails, names, and
      // contact form contents. Opt specific non-sensitive nodes out
      // with the `data-sentry-unmask` attribute rather than disabling
      // masks globally.
      Sentry.replayIntegration(),
    ],

    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // Only propagate trace headers to same-origin requests. The
    // api.capsulechichub.com Worker's CORS preflight does NOT list
    // `sentry-trace` / `baggage` in Access-Control-Allow-Headers, so
    // adding them to cross-origin API calls makes every request fail
    // preflight. Re-add the API host here once the Worker's CORS
    // config explicitly allows those two headers — until then,
    // distributed tracing is web-only.
    tracePropagationTargets: ['localhost', /^https:\/\/capsulechichub\.com/],

    replaysSessionSampleRate: import.meta.env.PROD ? 0.05 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Stale-chunk failures after a deploy are handled by lazyWithReload and
    // AppErrorBoundary (a budgeted reload picks up the new HTML and chunks).
    // They are not actionable — drop them so they don't reopen the issue
    // every deploy and burn quota.
    //
    // This list is meant to be the same set isChunkLoadError() matches in
    // @/lib/chunk-reload — keep them in step. Widened deliberately when that
    // predicate grew: an error we self-heal is an error we shouldn't page on.
    // Note ignoreErrors matches on message only, so the ChunkLoadError *name*
    // branch has no equivalent here; its messages are covered below.
    ignoreErrors: [
      /Failed to fetch dynamically imported module/i,
      /error loading dynamically imported module/i,
      /Importing a module script failed/i,
      /Unable to preload CSS/i,
      /ChunkLoadError/i,
      /Loading (CSS )?chunk .* failed/i,
      // CAPSULE-CHIC-HUB-WEB-8: a blob: worker on the page failed to
      // importScripts another blob:. This site creates no workers of its
      // own; the ones on the page belong to Sentry Replay and to whatever
      // the visitor's browser injects, and the one event came from a
      // generic Android device with no user. Not ours to fix.
      /Failed to execute 'importScripts' on 'WorkerGlobalScope'/,
    ],
  });
}
